/**
 * Copyright 2023 LiveKit, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Inlined from @livekit/egress-sdk (same behavior) so CRA does not load the
 * package’s broken source map (source-map-loader JSON parse error).
 */
import { ParticipantEvent, RoomEvent } from 'livekit-client';

let currentRoom;
let layoutChangedCallback;
let state = { layout: '' };
/** Egress only needs the first START_RECORDING; avoid duplicate console lines from multiple fallbacks. */
let startRecordingEmitted = false;

function getURLParam(name) {
  const query = new URLSearchParams(window.location.search);
  return query.get(name);
}

function onMetadataChanged() {
  const metadata = currentRoom?.localParticipant?.metadata;
  if (!metadata) return;
  try {
    const newState = JSON.parse(metadata);
    if (newState && newState.layout !== state.layout) {
      state = newState;
      if (typeof layoutChangedCallback === 'function') {
        layoutChangedCallback(state.layout);
      }
    }
  } catch {
    /* ignore invalid metadata */
  }
}

const EgressHelper = {
  getLiveKitURL() {
    const url = getURLParam('url');
    if (!url) throw new Error('url is not found in query string');
    return url;
  },

  getAccessToken() {
    const token = getURLParam('token');
    if (!token) throw new Error('token is not found in query string');
    return token;
  },

  getLayout() {
    if (state.layout) return state.layout;
    const layout = getURLParam('layout');
    return layout ?? '';
  },

  setRoom(room) {
    if (currentRoom) {
      currentRoom.off(RoomEvent.Disconnected, EgressHelper.endRecording);
      currentRoom.localParticipant?.off?.(
        ParticipantEvent.ParticipantMetadataChanged,
        onMetadataChanged
      );
    }
    currentRoom = room;
    if (currentRoom) {
      currentRoom.localParticipant?.on?.(
        ParticipantEvent.ParticipantMetadataChanged,
        onMetadataChanged
      );
      currentRoom.on(RoomEvent.Disconnected, EgressHelper.endRecording);
      onMetadataChanged();
    }
  },

  startRecording() {
    if (startRecordingEmitted) return;
    startRecordingEmitted = true;
    console.log('START_RECORDING');
  },

  endRecording() {
    currentRoom = undefined;
    console.log('END_RECORDING');
  },

  onLayoutChanged(f) {
    layoutChangedCallback = f;
  },
};

export default EgressHelper;
